// src/services/apiService.js
// Servicio para conectar el frontend React con el backend API

import { config } from '../config';
import { apiLog, errorLog, debugLog, warningLog } from '../config/featureFlags';

const API_URL = config.API_URL;

// Obtener el ambiente y mostrarlo en consola
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 
  (process.env.NODE_ENV === 'development' ? 'development' : 'production');
debugLog(`🔧 Ambiente de la aplicación: ${ENVIRONMENT}`);
debugLog(`🌐 API URL: ${API_URL}`);

// Helper para hacer peticiones con autenticación - devuelve Response completo
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('makana_token');
  
  const headers = {
    'Content-Type': 'application/json',
    'X-App-Environment': ENVIRONMENT, // Header para separar entornos
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Log detallado en desarrollo para verificar headers
  if (process.env.NODE_ENV === 'development' && apiLog) {
    apiLog(`📤 Petición a: ${API_URL}${url}`, {
      method: options.method || 'GET',
      headers: {
        'X-App-Environment': ENVIRONMENT,
        'Content-Type': headers['Content-Type'],
        'Authorization': token ? 'Bearer ***' : 'No token'
      }
    });
  }
  
  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });
  
  if (response.status === 401) {
    // Token expirado, limpiar y redirigir al login
    localStorage.removeItem('makana_token');
    localStorage.removeItem('makana_user');
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }
  
  // Devuelve el objeto Response completo para que cada función pueda validar status
  return response;
};

// Helper para hacer peticiones con autenticación y parsear JSON automáticamente
const fetchWithAuthJSON = async (url, options = {}) => {
  const response = await fetchWithAuth(url, options);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error en la petición' }));
    throw new Error(error.error || 'Error en la petición');
  }
  
  return response.json();
};

// ============================================
// AUTENTICACIÓN
// ============================================

export const authService = {
  // Registro
  register: async (username, password, email) => {
    const headers = {
      'Content-Type': 'application/json',
      'X-App-Environment': ENVIRONMENT, // Header para separar entornos
    };
    
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username, password, email }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al registrar');
    }
    
    return response.json();
  },
  
  // Login
  login: async (username, password) => {
    const headers = {
      'Content-Type': 'application/json',
      'X-App-Environment': ENVIRONMENT, // Header para separar entornos
    };
    
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al iniciar sesión');
    }
    
    const data = await response.json();
    
    // Guardar token y usuario en localStorage
    localStorage.setItem('makana_token', data.token);
    localStorage.setItem('makana_user', JSON.stringify(data.user));
    
    return data;
  },
  
  // Logout
  logout: () => {
    localStorage.removeItem('makana_token');
    localStorage.removeItem('makana_user');
    window.location.href = '/login';
  },
  
  // Verificar si está autenticado
  isAuthenticated: () => {
    return !!localStorage.getItem('makana_token');
  },
  
  // Obtener usuario actual
  getCurrentUser: () => {
    const userStr = localStorage.getItem('makana_user');
    return userStr ? JSON.parse(userStr) : null;
  },
};

// ============================================
// EN VIVOS
// ============================================

export const envivoService = {
  // Crear en vivo
  create: async (envivoData) => {
    apiLog('🚀 Creando en vivo con datos:', envivoData);
    try {
      const result = await fetchWithAuthJSON('/envivos', {
        method: 'POST',
        body: JSON.stringify(envivoData),
      });
      apiLog('✅ En vivo creado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error creando en vivo:', error);
      throw error;
    }
  },
  
  // Obtener todos los en vivos
  getAll: async () => {
    const response = await fetchWithAuth('/envivos');
    return response.json();
  },
  
  // ✅ OPTIMIZACIÓN: Obtener en vivos por viaje específico
  getByTrip: async (tripId) => {
    apiLog('🚀 Obteniendo en vivos del viaje:', tripId);
    try {
      const response = await fetchWithAuth(`/envivos?trip_id=${tripId}`);
      const result = await response.json();
      apiLog('✅ En vivos del viaje obtenidos:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo en vivos del viaje:', error);
      throw error;
    }
  },
  
  // Actualizar en vivo
  update: async (id, envivoData) => {
    apiLog('🚀 Actualizando en vivo:', id, envivoData);
    try {
      const result = await fetchWithAuthJSON(`/envivos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(envivoData),
      });
      apiLog('✅ En vivo actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando en vivo:', error);
      throw error;
    }
  },
  
  // Cerrar en vivo
  close: async (id) => {
    const endTime = new Date().toISOString();
    const response = await fetchWithAuth(`/envivos/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'closed', endTime }),
    });
    return response.json();
  },
  
  // Reabrir en vivo
  reopen: async (id) => {
    const response = await fetchWithAuth(`/envivos/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'active' }),
    });
    return response.json();
  },
};

// ============================================
// CLIENTES
// ============================================

export const clientService = {
  // NUEVO: Agregar clientes a un en vivo (crea automáticamente si no existen)
  addToEnvivo: async (envivoId, usernames) => {
    const response = await fetchWithAuth(`/envivos/${envivoId}/clients`, {
      method: 'POST',
      body: JSON.stringify({ clients: usernames }),
    });
    return response.json();
  },
  
  // Obtener clientes de un en vivo específico
  getByEnvivo: async (envivoId) => {
    apiLog('🚀 Obteniendo clientes del envivo:', envivoId);
    try {
      const result = await fetchWithAuthJSON(`/envivos/${envivoId}/clients`);
      apiLog('✅ Clientes del envivo obtenidos:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo clientes del envivo:', error);
      throw error;
    }
  },
  
  // Crear clientes por lote (método anterior - mantener por compatibilidad)
  createBatch: async (usernames) => {
    const response = await fetchWithAuth('/clients', {
      method: 'POST',
      body: JSON.stringify({ clients: usernames }),
    });
    return response.json();
  },
  
  // Obtener todos los clientes
  getAll: async () => {
    const response = await fetchWithAuth('/clients');
    return response.json();
  },

  // Actualizar cliente
  update: async (id, clientData) => {
    apiLog('🚀 Actualizando cliente con datos:', { id, clientData });
    try {
      const result = await fetchWithAuthJSON(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(clientData),
      });
      apiLog('✅ Cliente actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando cliente:', error);
      throw error;
    }
  },

  // Eliminar cliente
  delete: async (id) => {
    apiLog('🚀 Eliminando cliente con ID:', id);
    try {
      const result = await fetchWithAuthJSON(`/clients/${id}`, {
        method: 'DELETE',
      });
      apiLog('✅ Cliente eliminado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error eliminando cliente:', error);
      throw error;
    }
  },
};

// ============================================
// PRODUCTOS
// ============================================

export const productService = {
  // Crear producto
  create: async (name, basePrice, description, envivoId) => {
    apiLog('🚀 Creando producto con datos:', { name, basePrice, description, envivoId, basePriceType: typeof basePrice });
    try {
      const result = await fetchWithAuthJSON('/products', {
        method: 'POST',
        body: JSON.stringify({ name, basePrice, description, envivoId }),
      });
      apiLog('✅ Producto creado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error creando producto:', error);
      throw error;
    }
  },
  
  // Obtener todos los productos
  getAll: async () => {
    apiLog('🚀 Obteniendo todos los productos');
    try {
      const result = await fetchWithAuthJSON('/products');
      apiLog('✅ Productos obtenidos exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo productos:', error);
      throw error;
    }
  },

  // ✅ OPTIMIZACIÓN: Obtener productos por envivo específico
  getByEnvivo: async (envivoId) => {
    apiLog('🚀 Obteniendo productos del envivo:', envivoId);
    try {
      const response = await fetchWithAuth(`/products?envivo_id=${envivoId}`);
      const result = await response.json();
      apiLog('✅ Productos del envivo obtenidos:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo productos del envivo:', error);
      throw error;
    }
  },

  // Actualizar producto
  update: async (id, productData) => {
    apiLog('🚀 Actualizando producto con datos:', { id, productData });
    try {
      const result = await fetchWithAuthJSON(`/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(productData),
      });
      apiLog('✅ Producto actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando producto:', error);
      throw error;
    }
  },

  // Eliminar producto
  delete: async (id) => {
    apiLog('🚀 Eliminando producto con ID:', id);
    try {
      const result = await fetchWithAuthJSON(`/products/${id}`, {
        method: 'DELETE',
      });
      apiLog('✅ Producto eliminado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error eliminando producto:', error);
      throw error;
    }
  },
};

// ============================================
// PEDIDOS
// ============================================

export const orderService = {
  // Agregar producto a una orden (nueva estructura)
  addProduct: async (productData) => {
    apiLog('🚀 Agregando producto a orden con datos:', productData);
    try {
      const result = await fetchWithAuthJSON('/orders', {
        method: 'POST',
        body: JSON.stringify(productData),
      });
      apiLog('✅ Producto agregado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error agregando producto:', error);
      throw error;
    }
  },
  
  // Mantener método create por compatibilidad (deprecated)
  create: async (orderData) => {
    warningLog('⚠️ DEPRECATED: Usar orderService.addProduct en lugar de create');
    return orderService.addProduct(orderData);
  },
  
  // Obtener pedidos (con filtros opcionales)
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const queryString = params.toString();
    const response = await fetchWithAuth(`/orders${queryString ? '?' + queryString : ''}`);
    return response.json();
  },

  // ✅ OPTIMIZACIÓN: Obtener órdenes por envivo específico
  getByEnvivo: async (envivoId) => {
    apiLog('🚀 Obteniendo órdenes del envivo:', envivoId);
    try {
      const response = await fetchWithAuth(`/orders?envivo_id=${envivoId}`);
      const result = await response.json();
      apiLog('✅ Órdenes del envivo obtenidas:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo órdenes del envivo:', error);
      throw error;
    }
  },
  
  // Actualizar pedido completo
  update: async (id, orderData) => {
    apiLog('🚀 Actualizando orden con datos:', { id, orderData });
    apiLog('🔍 JSON payload:', JSON.stringify(orderData, null, 2));
    try {
      const result = await fetchWithAuthJSON(`/orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(orderData),
      });
      apiLog('✅ Orden actualizada exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando orden:', error);
      throw error;
    }
  },
  
  // Actualizar estado de pedido
  updateStatus: async (id, status) => {
    const response = await fetchWithAuth(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    return response.json();
  },
  
  // Actualizar delivery_status de una orden individual
  updateDeliveryStatus: async (orderId, deliveryStatus, changeReason = null) => {
    apiLog('🚀 Actualizando delivery_status de orden individual:', { orderId, deliveryStatus });
    apiLog('🔧 Ambiente enviado en header:', ENVIRONMENT);
    apiLog('🌐 URL completa:', `${API_URL}/orders/${orderId}/delivery-status`);
    try {
      const response = await fetchWithAuth(`/orders/${orderId}/delivery-status`, {
        method: 'PUT',
        body: JSON.stringify({
          delivery_status: deliveryStatus,
          change_reason: changeReason || `Actualización manual desde frontend - Orden: ${orderId}`
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al actualizar estado');
      }
      
      const data = await response.json();
      apiLog('✅ Delivery status de orden actualizado exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error actualizando delivery status de orden:', error);
      throw error;
    }
  },

  // Actualizar quick_checked (revisión rápida) de una orden individual
  updateQuickChecked: async (orderId, quickChecked) => {
    apiLog('🚀 Actualizando quick_checked de orden:', { orderId, quickChecked });
    try {
      const result = await fetchWithAuthJSON(`/orders/${orderId}/quick-check`, {
        method: 'PUT',
        body: JSON.stringify({
          quick_checked: !!quickChecked,
        }),
      });

      // El backend devuelve { message, order } según especificación
      const updatedOrder = result.order || result;
      apiLog('✅ quick_checked de orden actualizado exitosamente:', updatedOrder);
      return updatedOrder;
    } catch (error) {
      errorLog('❌ Error actualizando quick_checked de orden:', error);
      throw error;
    }
  },

  // Actualizar carry_on (viaja en maleta) de un item de orden
  updateCarryOn: async (itemId, carryOn) => {
    apiLog('🚀 Actualizando carry_on de item:', { itemId, carryOn });
    try {
      const result = await fetchWithAuthJSON(`/order-items/${itemId}/carry-on`, {
        method: 'PUT',
        body: JSON.stringify({
          carry_on: !!carryOn,
        }),
      });

      // El backend devuelve { message, order_item, order, client_username }
      apiLog('✅ carry_on de item actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando carry_on de item:', error);
      throw error;
    }
  },

  // Actualizar check_carry_on (empacado en maleta) de un item de orden
  updateCheckCarryOn: async (itemId, checkCarryOn) => {
    apiLog('🚀 Actualizando check_carry_on de item:', { itemId, checkCarryOn });
    try {
      const result = await fetchWithAuthJSON(`/order-items/${itemId}/check-carry-on`, {
        method: 'PUT',
        body: JSON.stringify({
          check_carry_on: !!checkCarryOn,
        }),
      });

      // El backend devuelve { message, order_item, client_username }
      apiLog('✅ check_carry_on de item actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando check_carry_on de item:', error);
      throw error;
    }
  },
  
  // Cancelar pedido
  cancel: async (id) => {
    const response = await fetchWithAuth(`/orders/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  // Eliminar item específico de una orden
  removeItem: async (orderId, itemIndex) => {
    apiLog('🚀 Eliminando item de orden:', { orderId, itemIndex });
    try {
      // Obtener la orden actual
      const orders = await orderService.getAll();
      const order = orders.find(o => o.id === orderId);
      
      if (!order) {
        throw new Error('Orden no encontrada');
      }

      // Crear nueva lista de items sin el item a eliminar
      const updatedItems = order.items.filter((_, index) => index !== itemIndex);
      
      // Recalcular total
      const newTotal = updatedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * (item.quantity || 1));
      }, 0);

      // Actualizar la orden
      const orderUpdateData = {
        items: updatedItems,
        total_amount: newTotal
      };

      const result = await orderService.update(orderId, orderUpdateData);
      apiLog('✅ Item eliminado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error eliminando item:', error);
      throw error;
    }
  },

  // Actualizar item específico de una orden
  updateItem: async (orderId, itemIndex, updatedItem) => {
    apiLog('🚀 Actualizando item de orden:', { orderId, itemIndex, updatedItem });
    try {
      // Obtener la orden actual
      const orders = await orderService.getAll();
      const order = orders.find(o => o.id === orderId);
      
      if (!order) {
        throw new Error('Orden no encontrada');
      }

      // Actualizar el item específico
      const updatedItems = [...order.items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        ...updatedItem
      };
      
      // Recalcular total
      const newTotal = updatedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * (item.quantity || 1));
      }, 0);

      // Actualizar la orden
      const orderUpdateData = {
        items: updatedItems,
        total_amount: newTotal
      };

      const result = await orderService.update(orderId, orderUpdateData);
      apiLog('✅ Item actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando item:', error);
      throw error;
    }
  },
};

// ============================================
// VIAJES
// ============================================

export const tripService = {
  // Crear viaje
  create: async (tripData) => {
    apiLog('🚀 Creando viaje con datos:', tripData);
    try {
      const result = await fetchWithAuthJSON('/trips', {
        method: 'POST',
        body: JSON.stringify(tripData),
      });
      apiLog('✅ Viaje creado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error creando viaje:', error);
      throw error;
    }
  },
  
  // Obtener todos los viajes
  getAll: async () => {
    const response = await fetchWithAuth('/trips');
    return response.json();
  },
  
  // Obtener viaje específico
  getById: async (id) => {
    const response = await fetchWithAuth(`/trips/${id}`);
    return response.json();
  },
  
  // Actualizar viaje
  update: async (id, tripData) => {
    apiLog('🚀 Actualizando viaje:', id, tripData);
    try {
      const result = await fetchWithAuthJSON(`/trips/${id}`, {
        method: 'PUT',
        body: JSON.stringify(tripData),
      });
      apiLog('✅ Viaje actualizado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error actualizando viaje:', error);
      throw error;
    }
  },
  
  // Eliminar viaje
  delete: async (id) => {
    const response = await fetchWithAuth(`/trips/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },
};

// ============================================
// BÚSQUEDA Y CONSOLIDADO
// ============================================

export const searchService = {
  // Búsqueda consolidada con filtros
  getConsolidated: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    
    const queryString = params.toString();
    const response = await fetchWithAuth(`/search/consolidated${queryString ? '?' + queryString : ''}`);
    return response.json();
  },
  
  // Resumen por viaje
  getTripSummary: async (tripId) => {
    const response = await fetchWithAuth(`/search/trip-summary/${tripId}`);
    return response.json();
  },
  
  // Resumen por en vivo
  getEnvivoSummary: async (envivoId) => {
    const response = await fetchWithAuth(`/search/envivo-summary/${envivoId}`);
    return response.json();
  },
};

// ============================================
// PAGOS Y COMPROBANTES
// ============================================

export const paymentService = {
  // Subir comprobante
  uploadReceipt: async (orderId, amount, paymentType, file, sendExcessToBag = false) => {
    const token = localStorage.getItem('makana_token');
    
    const formData = new FormData();
    formData.append('orderId', orderId);
    formData.append('amount', amount);
    formData.append('paymentType', paymentType);
    if (file) {
      formData.append('comprobante', file);
    }
    formData.append('send_excess_to_bag', sendExcessToBag.toString());
    
    const response = await fetch(`${API_URL}/payments/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-App-Environment': ENVIRONMENT, // Header para separar entornos
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al subir comprobante');
    }
    
    return response.json();
  },
  
  // Obtener comprobantes de un pedido
  getByOrder: async (orderId) => {
    const response = await fetchWithAuth(`/payments/order/${orderId}`);
    return response.json();
  },
  
  // ✅ NUEVO: Editar pago existente
  update: async (paymentId, amount, paymentType) => {
    apiLog('🚀 Editando pago con datos:', { paymentId, amount, paymentType });
    try {
      const result = await fetchWithAuthJSON(`/payments/${paymentId}`, {
        method: 'PUT',
        body: JSON.stringify({ amount, paymentType }),
      });
      apiLog('✅ Pago editado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error editando pago:', error);
      throw error;
    }
  },
  
  // ✅ NUEVO: Eliminar pago
  delete: async (paymentId) => {
    apiLog('🚀 Eliminando pago con ID:', paymentId);
    try {
      const result = await fetchWithAuthJSON(`/payments/${paymentId}`, {
        method: 'DELETE',
      });
      apiLog('✅ Pago eliminado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error eliminando pago:', error);
      throw error;
    }
  },
};

// ============================================
// ABONOS EN BOLSA (ADVANCE PAYMENTS)
// ============================================

export const advancePaymentService = {
  // Crear abono en bolsa
  create: async (clientId, advanceData) => {
    apiLog('🚀 Creando abono en bolsa:', { clientId, advanceData });
    try {
      const result = await fetchWithAuthJSON(`/clients/${clientId}/advance-payments`, {
        method: 'POST',
        body: JSON.stringify(advanceData),
      });
      apiLog('✅ Abono en bolsa creado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error creando abono en bolsa:', error);
      throw error;
    }
  },
  
  // Obtener saldo de bolsa
  getBalance: async (clientId, tripId = null) => {
    apiLog('🚀 Obteniendo saldo de bolsa:', { clientId, tripId });
    try {
      const url = tripId 
        ? `/clients/${clientId}/advance-balance?trip_id=${tripId}`
        : `/clients/${clientId}/advance-balance`;
      const result = await fetchWithAuthJSON(url);
      apiLog('✅ Saldo de bolsa obtenido exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo saldo de bolsa:', error);
      throw error;
    }
  },
  
  // Obtener historial de uso de un abono
  getUsageHistory: async (clientId, advancePaymentId) => {
    apiLog('🚀 Obteniendo historial de uso:', { clientId, advancePaymentId });
    try {
      const result = await fetchWithAuthJSON(`/clients/${clientId}/advance-payments/${advancePaymentId}/usage`);
      apiLog('✅ Historial de uso obtenido exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error obteniendo historial de uso:', error);
      throw error;
    }
  },
  
  // Cancelar abono en bolsa
  cancel: async (clientId, advancePaymentId, cancellationReason = null) => {
    apiLog('🚀 Cancelando abono en bolsa:', { clientId, advancePaymentId, cancellationReason });
    try {
      const result = await fetchWithAuthJSON(`/clients/${clientId}/advance-payments/${advancePaymentId}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({
          cancellation_reason: cancellationReason || null
        }),
      });
      apiLog('✅ Abono en bolsa cancelado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error cancelando abono en bolsa:', error);
      throw error;
    }
  },
  
  // Reembolsar abono en bolsa
  refund: async (clientId, advancePaymentId, cancellationReason = null) => {
    apiLog('🚀 Reembolsando abono en bolsa:', { clientId, advancePaymentId, cancellationReason });
    try {
      const result = await fetchWithAuthJSON(`/clients/${clientId}/advance-payments/${advancePaymentId}/refund`, {
        method: 'PUT',
        body: JSON.stringify({
          cancellation_reason: cancellationReason || null
        }),
      });
      apiLog('✅ Abono en bolsa reembolsado exitosamente:', result);
      return result;
    } catch (error) {
      errorLog('❌ Error reembolsando abono en bolsa:', error);
      throw error;
    }
  },
};

// ============================================
// REPORTES Y KPIS
// ============================================

export const reportService = {
  // Obtener KPIs
  getKPIs: async (envivoId = null) => {
    const params = envivoId ? `?envivoId=${envivoId}` : '';
    const response = await fetchWithAuth(`/reports/kpis${params}`);
    return response.json();
  },
  
  // Exportar a Excel
  exportToExcel: async (envivoId = null) => {
    const token = localStorage.getItem('makana_token');
    const params = envivoId ? `?envivoId=${envivoId}` : '';
    
    const response = await fetch(`${API_URL}/reports/export${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-App-Environment': ENVIRONMENT, // Header para separar entornos
      },
    });
    
    if (!response.ok) {
      throw new Error('Error al exportar datos');
    }
    
    // Descargar archivo
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_makana_${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

// ============================================
// REPORTE DE BANCOS
// ============================================

export const banksReportService = {
  // Resumen general (KPIs y gráficas globales)
  getSummary: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    const response = await fetchWithAuth(`/reports/banks/summary${qs ? '?' + qs : ''}`);
    return response.json();
  },
  // Detalle por en vivo
  getByEnvivo: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.envivoId) params.append('envivoId', filters.envivoId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    const response = await fetchWithAuth(`/reports/banks/by-envivo${qs ? '?' + qs : ''}`);
    return response.json();
  },
  // Detalle por banco/tarjeta
  getByBank: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    const response = await fetchWithAuth(`/reports/banks/by-bank${qs ? '?' + qs : ''}`);
    return response.json();
  },
  // Listado crudo de pagos al cierre (envivo_payments)
  getEnvivoPayments: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.envivoId) params.append('envivoId', filters.envivoId);
    if (filters.tripId) params.append('tripId', filters.tripId);
    const qs = params.toString();
    const response = await fetchWithAuth(`/reports/banks/envivo-payments${qs ? '?' + qs : ''}`);
    return response.json();
  },
  // Recepción de pagos por banco y por en vivo (order payments)
  getOrderPaymentsByEnvivo: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.envivoId) params.append('envivoId', filters.envivoId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    const response = await fetchWithAuth(`/reports/banks/order-payments-by-envivo${qs ? '?' + qs : ''}`);
    return response.json();
  },
};

// ============================================
// SERVICIO DE SALUD
// ============================================

export const healthService = {
  check: async () => {
    const response = await fetch(`${API_URL}/health`, {
      headers: {
        'X-App-Environment': ENVIRONMENT, // Header para separar entornos
      },
    });
    return response.json();
  },
};

// ============================================
// FEATURE FLAGS PÚBLICOS
// ============================================

const FEATURE_FLAGS_ENDPOINT = process.env.REACT_APP_FEATURE_FLAGS_ENDPOINT || '/feature-flags/public';

export const featureFlagService = {
  // Obtener feature flags públicos desde backend
  getPublic: async () => {
    const response = await fetch(`${API_URL}${FEATURE_FLAGS_ENDPOINT}`, {
      headers: {
        'X-App-Environment': ENVIRONMENT,
      },
    });

    if (!response.ok) {
      throw new Error(`No se pudieron obtener feature flags públicos (${response.status})`);
    }

    return response.json();
  },
};

// ============================================
// SERVICIO DE LOGÍSTICA
// ============================================

// Función eliminada: getSimulatedLogisticsData
// Ahora solo usamos datos reales del backend

export const logisticsService = {
  // Obtener control logístico por viaje (NUEVO - nivel de producto)
  getTripControl: async (tripId) => {
    apiLog('🚀 Obteniendo control logístico del viaje:', tripId);
    try {
      const response = await fetchWithAuth(`/logistics/trip-control/${tripId}`);
      const data = await response.json();
      apiLog('✅ Control logístico obtenido exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error obteniendo control logístico:', error);
      // Sin fallback - solo datos reales
      apiLog('❌ Error obteniendo control logístico del viaje');
      throw error;
    }
  },

  // Actualizar estado de UN producto específico (NUEVO)
  updateProductStatus: async (orderItemId, statusData) => {
    apiLog('🚀 Actualizando estado de producto:', { orderItemId, statusData });
    try {
      const response = await fetchWithAuth(`/order-items/${orderItemId}/arrival-status`, {
        method: 'PUT',
        body: JSON.stringify({
          arrival_status: statusData.arrival_status,
          change_reason: statusData.change_reason || `Actualización manual desde frontend`
        })
      });
      const data = await response.json();
      apiLog('✅ Estado de producto actualizado exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error actualizando estado de producto:', error);
      throw error;
    }
  },

  // Actualizar estado de TODOS los productos de una orden (LEGACY)
  updateOrderStatus: async (orderId, statusData) => {
    apiLog('🚀 Actualizando estado de orden completa:', { orderId, statusData });
    try {
      const response = await fetchWithAuth(`/orders/${orderId}/arrival-status`, {
        method: 'PUT',
        body: JSON.stringify({
          arrival_status: statusData.arrival_status,
          change_reason: statusData.change_reason || `Actualización manual de orden completa`
        })
      });
      const data = await response.json();
      apiLog('✅ Estado de orden actualizado exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error actualizando estado de orden:', error);
      throw error;
    }
  },

  // NUEVO: Actualizar delivery_status de TODAS las órdenes de un cliente en un viaje
  updateClientTripDelivery: async (clientUsername, tripId, deliveryStatus) => {
    apiLog('🚀 Actualizando delivery_status por cliente y viaje:', { clientUsername, tripId, deliveryStatus });
    apiLog('🔧 Ambiente enviado en header:', ENVIRONMENT);
    apiLog('🌐 URL completa:', `${API_URL}/logistics/client-trip-delivery/${clientUsername}/${tripId}`);
    try {
      const response = await fetchWithAuth(`/logistics/client-trip-delivery/${clientUsername}/${tripId}`, {
        method: 'PUT',
        body: JSON.stringify({
          delivery_status: deliveryStatus,
          change_reason: `Actualización manual desde frontend - Cliente: ${clientUsername}, Viaje: ${tripId}`
        })
      });
      const data = await response.json();
      apiLog('✅ Delivery status actualizado exitosamente:', data);
      apiLog('📊 Órdenes afectadas:', data.affected_orders);
      apiLog('📋 IDs de órdenes:', data.order_ids);
      // Log adicional para verificar entorno
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 VERIFICACIÓN DE ENTORNO:');
        console.log('   - NODE_ENV: development');
        console.log('   - ENVIRONMENT:', ENVIRONMENT);
        console.log('   - Header X-App-Environment enviado:', ENVIRONMENT);
        console.log('   - Si el header es "development", el backend DEBE usar tablas dev_*');
        console.log('   - Verifica en DevTools → Network → Headers → Request Headers');
      }
      return data;
    } catch (error) {
      errorLog('❌ Error actualizando delivery status:', error);
      throw error;
    }
  },

  // Obtener reporte de control
  getControlReport: async (targetId, filters = {}) => {
    apiLog('🚀 Obteniendo reporte de control logístico:', { targetId, filters });
    try {
      // Construir parámetros de query, filtrando valores vacíos
      const queryParams = new URLSearchParams();
      
      // Agregar tripId si está presente
      if (filters.tripId && filters.tripId !== '' && filters.tripId !== 'Todos los viajes') {
        queryParams.append('tripId', filters.tripId);
      }
      
      // Agregar envivo_id si está presente
      if (filters.envivoId && filters.envivoId !== '' && filters.envivoId !== 'Todos los en vivos') {
        queryParams.append('envivo_id', filters.envivoId);
      }
      
      // Agregar exclusive_to_envivo si está activo
      if (filters.exclusiveToEnvivo === true) {
        queryParams.append('exclusive_to_envivo', 'true');
      }
      
      // Agregar otros filtros solo si tienen valores válidos
      Object.entries(filters).forEach(([key, value]) => {
        // Excluir exclusiveToEnvivo porque ya lo manejamos arriba
        if (key !== 'tripId' && key !== 'envivoId' && key !== 'exclusiveToEnvivo' &&
            value !== '' && value !== null && value !== undefined && 
            value !== 'Todos los en vivos' && value !== 'Todos los viajes' && 
            value !== 'Todos los estados') {
          queryParams.append(key, value);
        }
      });
      
      const queryString = queryParams.toString();
      const url = queryString ? `/logistics/control-report?${queryString}` : '/logistics/control-report';
      
      apiLog('🌐 URL de consulta:', url);
      
      const response = await fetchWithAuth(url);
      
      // Verificar si la respuesta es válida
      if (!response) {
        apiLog('❌ No hay respuesta del servidor');
        throw new Error('No hay respuesta del servidor');
      }
      
      // Verificar si la respuesta es exitosa usando status HTTP
      apiLog('🔍 Response status:', response.status);
      apiLog('🔍 Response statusText:', response.statusText);
      
      // Validar que el status sea 200 (éxito)
      if (response.status !== 200) {
        apiLog('❌ Respuesta no exitosa:', response.status, response.statusText);
        throw new Error(`Error del servidor: ${response.status} ${response.statusText}`);
      }
      
      apiLog('✅ Respuesta exitosa del servidor');
      
      const data = await response.json();
      
      // Validar estructura JSON según especificación del usuario
      if (!data || typeof data !== 'object') {
        throw new Error('Respuesta del servidor no es un objeto JSON válido');
      }
      
      if (!Array.isArray(data.products)) {
        throw new Error('La respuesta no contiene un array de products válido');
      }
      
      apiLog('✅ Reporte de control obtenido exitosamente:');
      apiLog('✅ Products count:', data.products.length);
      apiLog('✅ Statistics:', data.statistics);
      apiLog('✅ Estructura JSON válida según especificación');
      
      return data;
    } catch (error) {
      errorLog('❌ Error obteniendo reporte de control:', error);
      throw error;
    }
  },

  // Obtener historial de cambios
  getHistory: async (orderId) => {
    apiLog('🚀 Obteniendo historial de orden:', orderId);
    try {
      const response = await fetchWithAuth(`/logistics/history/${orderId}`);
      const data = await response.json();
      apiLog('✅ Historial obtenido exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error obteniendo historial:', error);
      throw error;
    }
  },

  // Obtener estadísticas de tiempos
  getTimingStats: async (envivoId) => {
    apiLog('🚀 Obteniendo estadísticas de tiempos:', envivoId);
    try {
      const response = await fetchWithAuth(`/logistics/timing-stats?envivo_id=${envivoId}`);
      const data = await response.json();
      apiLog('✅ Estadísticas de tiempos obtenidas exitosamente:', data);
      return data;
    } catch (error) {
      errorLog('❌ Error obteniendo estadísticas de tiempos:', error);
      throw error;
    }
  },
};

// Exportar todos los servicios como default
const apiService = {
  auth: authService,
  envivo: envivoService,
  client: clientService,
  product: productService,
  order: orderService,
  trip: tripService,
  search: searchService,
  payment: paymentService,
  advancePayment: advancePaymentService,
  report: reportService,
  banksReport: banksReportService,
  health: healthService,
  featureFlags: featureFlagService,
  logistics: logisticsService,
};

export default apiService;
